// src/types/models.ts
// Analytics System - Domain Models
//
// TRANSLATION USAGE:
// Many helper functions accept an optional translate parameter for i18n support.
// Example:
//   import { useTranslate } from 'react-admin';
//   import { getErrorTypeInfo, getSectionInfo } from '@/types/models';
//   
//   const MyComponent = () => {
//     const translate = useTranslate();
//     const errorInfo = getErrorTypeInfo('Generic', translate);
//     const sectionInfo = getSectionInfo('S', translate);
//     return <div>{errorInfo.name} - {sectionInfo.name}</div>;
//   };

// ============================================
// BASE TYPES AND ENUMS
// ============================================

export type UserRole = 'SuperAdmin' | 'Admin' | 'GroupAdmin' | 'HotelAdmin' | 'Receptionist' | 'Viewer';

export type DeviceType = 'Kiosk' | 'Dispenser' | 'Mobile' | 'Virtual' | 'OnlineCheckIn' | 'Other';

export type DeviceStatus = 'PendingActivation' | 'Active' | 'Inactive';

export type DeviceSource = 'Manual' | 'Updater';

/**
 * Channel - Where the guest interaction happened
 * 
 * Values match backend Channel enum exactly
 */
export type Channel =
  | 'Online'              // Pre-check-in (web)
  | 'Kiosk'               // Physical kiosk
  | 'AirLobby'            // Air Lobby (formerly "MobileReceptionist" / mobile desk)
  | 'MobileKiosk'         // Mobile app in kiosk mode
  | 'Dispenser';          // Key dispenser

export type ChannelDistribution = Record<Channel, number>;

export type CheckInType = 'Reservation' | 'Walking';

export type AlertType = 'DeviceOffline' | 'HighErrorRate' | 'LowCompletionRate' | 'SystemError';

export type AlertStatus = 'Active' | 'Acknowledged' | 'Resolved';

export type EventCode = 
  | 'HB'          // Heartbeat
  | 'CI_START'    // Check-in started
  | 'CI_SEARCH'   // Reservation search/selection
  | 'CI_IDENTIFY' // Guest identification
  | 'CI_SIGN'     // Signature capture
  | 'CI_UPSELL'   // Upselling
  | 'CI_COMP'     // Check-in completed
  | 'PC_LINK_OPEN' // Pre-checkin link opened (guest opened the check-in link)
  | 'PC_START'    // Pre-checkin started
  | 'PC_COMP'     // Pre-checkin completed
  | 'PM_SUCCESS'  // Payment successful
  | 'PM_ERROR'    // Payment error
  | 'KD_SUCCESS'  // Key dispensed successfully
  | 'KD_ERROR'    // Key dispense error
  | 'ERR'         // Generic error
  | 'AB';         // Abandoned

export type SectionCode = 
  | 'S'   // Start
  | 'I'   // Identification
  | 'F'   // Form
  | 'U'   // Upsell
  | 'P'   // Payment
  | 'K';  // Key

// ============================================
// USERS AND AUTHENTICATION
// ============================================

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  hotelId?: string;
  groupId?: string;
  hotelName?: string;
  groupName?: string;
  /** Array of assigned group IDs (for GroupAdmin, Viewer) */
  assignedGroups?: string[];
  /** Array of assigned hotel IDs (for HotelAdmin, Viewer) */
  assignedHotels?: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  hotelId?: string;
  groupId?: string;
  /** Array of assigned group IDs (for GroupAdmin, Viewer) */
  assignedGroups?: string[];
  /** Array of assigned hotel IDs (for HotelAdmin, Viewer) */
  assignedHotels?: string[];
  /**
   * Capabilities granted independently of the role (Decision 7 of the
   * hotel-operations redesign). Today only 'technical' exists: it adds the
   * Técnico rail group to whichever role the user has. Read from the token's
   * client roles (`insight:technical`) or from `GET /api/v1/ui/users/me`
   * when that endpoint is available.
   */
  capabilities?: string[];
  token: string;
  expiresIn: number;
}

// ============================================
// GROUPS
// ============================================

export interface Group {
  id: string;
  name: string;
  description?: string;
  hotels?: HotelSummary[];
  totalDevices?: number;
  activeDevices?: number;
  isActive?: boolean;
  createdAt: string;
  updatedAt?: string;
}

// Request DTOs for Groups
export interface CreateGroupRequest {
  id?: string; // Optional, backend generates GUID if not provided
  name: string;
  description?: string;
}

export interface UpdateGroupRequest {
  name?: string;
  description?: string;
}

// ============================================
// HOTELS
// ============================================

export type HotelSource = 'Manual' | 'Updater';

export interface Hotel {
  id: string;
  name: string;
  groupId?: string;
  groupName?: string;
  devices?: DeviceSummary[];
  deviceCount: number;
  activeDevices: number;
  analyticsEnabled?: boolean;
  source?: HotelSource;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface HotelSummary {
  id: string;
  name: string;
  deviceCount: number;
  isActive: boolean;
}

export interface HotelDetail extends Hotel {
  checkInsToday: number;
  checkInsThisWeek: number;
  checkInsThisMonth: number;
  hasRecentCheckIns: boolean;
  statistics?: HotelStatistics;
}

export interface HotelStatistics {
  hotelId: string;
  calculatedAt: string;
  
  // Device stats
  totalDevices: number;
  activeDevices: number;
  offlineDevices: number;
  
  // Check-in stats
  checkInsToday: number;
  checkInsYesterday: number;
  checkInsThisWeek: number;
  checkInsLastWeek: number;
  checkInsThisMonth: number;
  checkInsLastMonth: number;
  
  // Performance metrics
  averageCheckInTimeSeconds: number;
  preCheckInCompletionRate: number;
  keysDispensedAutomatically: number;
  
  // Channel distribution
  channelDistribution: Record<string, number>;
  channelPercentages: Record<string, number>;
  
  // Journeys
  completeDigitalJourneys: number;
  digitalJourneyPercentage: number;
  
  // Errors (only for SuperAdmin)
  totalErrors?: number;
  abandonedSessions?: number;
}

// Request DTOs for Hotels
export interface CreateHotelRequest {
  id?: string; // Optional, backend generates GUID if not provided
  name: string;
  groupId?: string;
}

export interface UpdateHotelRequest {
  name?: string;
  groupId?: string;
  isActive?: boolean;
}

// ============================================
// NOTIFIER CONFIGURATION TYPES
// ============================================

/**
 * Alert mapping - Links alert types to templates or custom messages
 */
export interface AlertMapping {
  alertType: string;
  templateId?: string;
  useCustomMessage: boolean;
  customSubject?: string;
  customMessage?: string;
}

/**
 * Channel configuration - Email, SMS, WhatsApp
 */
export interface ChannelConfig {
  type: string;
  enabled: boolean;
  recipients: string[];
  alertMappings: AlertMapping[];
}

/**
 * Notifier configuration for a hotel
 */
export interface NotifierConfiguration {
  apiKey: string;
  accountName: string;
  channels: ChannelConfig[];
}

/**
 * Template from Notifier API
 */
export interface NotifierTemplate {
  id: string;
  name: string;
  description: string;
  channelType: string;
  variables: string[];
}

/**
 * Template variable from Notifier API
 */
export interface NotifierTemplateVariable {
  name: string;
  description: string;
  isRequired: boolean;
  defaultValue?: string;
}

/**
 * Request to update notifier configuration
 */
export interface UpdateNotifierConfigRequest {
  apiKey: string;
  accountName: string;
  channels: {
    type: string;
    enabled: boolean;
    recipients: string[];
    alertMappings: {
      alertType: string;
      templateId?: string;
      useCustomMessage: boolean;
      customSubject?: string;
      customMessage?: string;
    }[];
  }[];
}

/**
 * Available alert types
 */
export const ALERT_TYPES = [
  'DeviceOffline',
  'HighErrorRate',
  'LowCompletionRate',
  'SystemError',
] as const;

export type NotifierAlertType = typeof ALERT_TYPES[number];

/**
 * Available channel types
 */
export const CHANNEL_TYPES = [
  'EMAIL',
  'SMS',
  'WHATSAPP',
] as const;

export type NotifierChannelType = typeof CHANNEL_TYPES[number];

/**
 * Helper to get channel icon
 */
export const getChannelIcon = (channelType: string): string => {
  switch (channelType.toUpperCase()) {
    case 'EMAIL':
      return '📧';
    case 'SMS':
      return '💬';
    case 'WHATSAPP':
      return '📱';
    default:
      return '📢';
  }
};

/**
 * Helper to get alert type color
 */
export const getAlertTypeColor = (alertType: string): 'error' | 'warning' | 'info' => {
  switch (alertType) {
    case 'DeviceOffline':
    case 'SystemError':
      return 'error';
    case 'HighErrorRate':
    case 'LowCompletionRate':
      return 'warning';
    default:
      return 'info';
  }
};

// ============================================
// ANALYTICS ENTITIES
// ============================================

/**
 * Device entity - represents kiosks, dispensers, and other check-in devices
 */
/** A key dispenser whose operator code the user may generate (GET devices/operator-code/dispensers). */
export interface OperatorCodeDispenser {
  id: string;
  installationCode: string;
  name?: string | null;
  hotelId: string;
}

export interface Device {
  id: string;
  installationCode: string;
  hotelId: string;
  groupId?: string;
  deviceType: DeviceType;
  channel: Channel;
  status: DeviceStatus;
  source: DeviceSource;
  registeredAt: string;
  lastSeenAt?: string;
  updatedAt?: string;
  serialNumber?: string;
  name?: string;
  metadata?: Record<string, string>;
}

/**
 * DeviceStatus entity - real-time device monitoring
 * Matches DeviceStatusDto from backend API
 */
export interface DeviceStatusEntity {
  deviceId: string;           // Installation code
  deviceType: string;
  hotelId: string;
  currentSection?: string | null;
  lastEventAt: string;        // ISO date string
  isActive: boolean;
  minutesSinceLastEvent: number;
}

/**
 * Form data for creating a device manually (SuperAdmin only)
 */
export interface DeviceCreateFormData {
  installationCode: string;
  hotelId: string;
  groupId?: string;
  deviceType: DeviceType;
  name?: string;
}

/**
 * Form data for updating a device (SuperAdmin only)
 */
export interface DeviceUpdateFormData {
  hotelId?: string;
  groupId?: string | null;
  status?: DeviceStatus;
  name?: string;
}

export interface DeviceSummary {
  installationCode: string;
  deviceType: string;
  status: string;
  lastEventAt?: string;
}

/**
 * Event entity - represents individual events sent by devices
 */
export interface Event {
  id: string;
  eventCode: EventCode;
  /** Null for online/service events (no physical device involved). */
  deviceId: string | null;
  sectionCode?: SectionCode;
  reservationNumber?: string;
  roomNumber?: string | null;

  /**
   * Additional reservation locators (OPTIONAL)
   * Used to link the same booking across different systems
   * Example: ["BKNG123456", "RES-2024-001"]
   */
  alternateReservationNumbers?: string[];

  channel: Channel;
  hotelId: string;
  groupId?: string;
  errorCode?: string;
  metadata?: Record<string, any>;
  timestamp: string;
  createdAt: string;
  receivedAt?: string;
}

/** Outcome of a notification delivery attempt (audit trail). */
export type NotificationStatus = 'Sent' | 'Skipped' | 'Failed';

/**
 * A persisted notification outcome — who was notified for an event/alert, or why nobody was.
 * Per-event notifications carry `eventId`; aggregated alerts carry `alertId`.
 */
export interface NotificationRecord {
  id: string;
  eventId?: string | null;
  alertId?: string | null;
  hotelId: string;
  deviceId?: string | null;
  errorCode?: string | null;
  notificationType: string;
  severity: string;
  channel: string;
  recipients: string[];
  status: NotificationStatus;
  statusReason?: string | null;
  subject?: string | null;
  createdAt: string;
  sentAt?: string | null;
}

/**
 * CheckInRecord entity - represents completed check-ins
 */
export interface CheckInRecord {
  id: string;
  reservationNumber: string;
  
  /**
   * Additional reservation locators
   * Used to link the same booking across different systems
   * Example: ["BKNG123456", "RES-2024-001", "ABC123"]
   * Display format: "BKNG123456 / RES-2024-001 / ABC123"
   */
  alternateReservationNumbers: string[];
  
  hotelId: string;
  groupId?: string;
  guestNameMasked: string;    // e.g., "Juan G."
  documentMasked: string;     // e.g., "X1234***"
  roomNumber?: string;
  checkInDate: string;
  checkOutDate?: string;
  amountCharged?: number;
  currency?: string;
  paymentMethod?: string;
  paymentOperationId?: string;
  channel: Channel;
  deviceId: string;
  type: CheckInType;
  duration?: number;          // Duration in seconds
  completedAt: string;
  createdAt: string;
}

/**
 * Journey - Complete guest check-in journey
 * 
 * Represents all touchpoints from pre-check-in to key delivery.
 * A journey is COMPLETE when check-in is done (checkInCompletedAt exists).
 * Key dispensing is OPTIONAL and tracked separately.
 * 
 * LOCATORS:
 * - reservationNumber is the primary locator (can be PMS, OTA, or any)
 * - alternateReservationNumbers stores additional locators for the same booking
 * - This allows linking pre-check-in (OTA) with check-in (PMS) correctly
 */
export interface Journey {
  id: string;
  reservationNumber: string | null;
  alternateReservationNumbers: string[];
  hotelId: string;
  roomNumber: string | null;
  isWalkIn: boolean;
  metadata?: Record<string, string>;
  
  // Timeline
  preCheckInStartedAt: string | null;
  preCheckInDeviceId: string | null;
  preCheckInCompletedAt: string | null;
  preCheckInCompletedDeviceId: string | null;
  checkInStartedAt: string | null;
  checkInCompletedAt: string | null;
  checkInDeviceId: string | null;
  keyDispensedAt: string | null;
  keyDispenserDeviceId: string | null;
  abandonedAt: string | null;
  abandonedAtSection: string | null;

  // Journey info
  initialChannel: Channel;
  finalChannel: Channel | null;
  channelsUsed: Channel[];
  
  // Status - NEW FIELD
  status: JourneyStatus;

  // How the journey is identified / originated. Generic across venue types — never a
  // placeholder. Drives the localized identity label (see getJourneyIdentityLabel).
  kind: JourneyKind;

  // Legacy fields (still present for backwards compatibility)
  isComplete: boolean;
  stepCount: number;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  
  // Computed fields
  displayIdentifier: string;
  isOrphaned: boolean; // Deprecated, use status === 'Abandoned'
  durationMinutes?: number;
}

/**
 * One channel "attempt" reconstructed for a journey's event log: a contiguous
 * block of activity on a single channel (e.g. the online pre-check-in, then a
 * later kiosk check-in) with its own section trail and outcome. Replaces the
 * per-device-only grouping for channels that have no device (Online, AirLobby,
 * MobileKiosk journeys).
 */
export interface JourneyAttempt {
  journeyId: string;
  channel: Channel;
  startedAt: string;
  endedAt: string | null;
  sections: string[];
  outcome: 'completed' | 'abandoned' | 'open';
}

/**
 * Response of GET /journeys/{id}/events: the reconstructed event log for a
 * journey (matched by reservation number / alternates / metadata.journeyId
 * within a time window) plus the attempt breakdown used to group them.
 */
export interface JourneyEvents {
  journeyId: string;
  events: Event[];
  attempts: JourneyAttempt[];
}

/**
 * Journey filter request for API calls
 */
export interface JourneyFilterRequest {
  hotelId?: string;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  isComplete?: boolean;
  page?: number;
  pageSize?: number;
}

export type JourneyStatus = 'Complete' | 'KeyOnly' | 'InProgress' | 'Abandoned';

/**
 * How a journey is identified and originated. Generic across venue types
 * (hotels, ships and any future installation):
 * - Reserved: tied to a reservation locator (PMS / OTA / any booking number)
 * - WalkIn: no prior reservation, correlated by unit + time
 * - DispenserOnly: standalone key dispensing with no reservation and no check-in
 *   (dispenser-only / no-reservation installations). Never a fabricated placeholder.
 */
export type JourneyKind = 'Reserved' | 'WalkIn' | 'DispenserOnly';

/**
 * Journey statistics/analytics
 */
export interface JourneyStats {
  totalJourneys: number;
  completeJourneys: number;
  partialJourneys: number;
  completionRate: number;
  avgDurationMinutes?: number;
}

/**
 * Update journey request (from devices)
 */
export interface UpdateJourneyRequest {
  reservationNumber: string;
  
  /**
   * Additional reservation locators (OPTIONAL)
   * Used when the device knows multiple locators for the same booking
   * Example: ["BKNG123456", "RES-2024-001"]
   */
  alternateReservationNumbers?: string[];
  
  eventType: 'PreCheckInStarted' | 'PreCheckInCompleted' | 'CheckInCompleted' | 'KeyDispensed';
  channel: Channel;
  timestamp?: string;
}

/**
 * DailyAnalytics entity - aggregated daily statistics
 */
export interface DailyAnalytics {
  id: string;
  hotelId: string;
  groupId?: string;
  date: string;
  
  // Check-in totals by channel
  totalCheckIns: number;
  onlineCheckIns: number;
  kioskCheckIns: number;
  receptionistCheckIns: number;
  mobileKioskCheckIns: number;
  
  // Pre-check-ins
  preCheckInsStarted: number;
  preCheckInsCompleted: number;
  
  // Keys
  keysDispensed: number;
  
  // Walkings
  walkingCheckIns: number;
  
  // Payments
  totalAmountCharged: number;
  
  // Journey metrics
  completeJourneys: number;
  partialJourneys: number;
  
  // Average times (in seconds)
  avgKioskCheckInTime?: number;
  
  // Error metrics (SuperAdmin only)
  errorCount?: number;
  abandonmentCount?: number;
  
  createdAt: string;
}

/**
 * MonthlyAnalytics entity - aggregated monthly statistics
 */
export interface MonthlyAnalytics {
  id: string;
  hotelId: string;
  groupId?: string;
  month: number;
  year: number;
  
  // Check-in totals
  totalCheckIns: number;
  onlineCheckIns: number;
  kioskCheckIns: number;
  receptionistCheckIns: number;
  mobileKioskCheckIns: number;
  
  // Pre-check-ins
  preCheckInsStarted: number;
  preCheckInsCompleted: number;
  
  // Keys
  keysDispensed: number;
  
  // Payments
  totalAmountCharged: number;
  
  // Journey metrics
  completeJourneys: number;
  partialJourneys: number;
  
  // Average times
  avgKioskCheckInTime?: number;
  avgJourneyDuration?: number;
  
  // Error metrics
  errorCount?: number;
  abandonmentCount?: number;
  
  createdAt: string;
}

/**
 * Alert entity - system alerts for monitoring
 */
/** Alert severity as the API sends it (AlertSeverity enum, serialised by name). */
export type AlertSeverity = 'Info' | 'Warning' | 'Critical';

export interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  status: AlertStatus;
  hotelId?: string;
  hotelName?: string;
  groupId?: string;
  groupName?: string;
  deviceId?: string;
  metadata?: Record<string, any>;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// OPERATION TYPES
// ============================================

export type OperationType = 'PreCheckIn' | 'CheckIn' | 'KeyDispensing';
export type OperationStatus = 'Completed' | 'Started' | 'Error' | 'Unknown';

export interface Operation {
  id: string;
  operationType: OperationType;
  eventCode: string;
  status: OperationStatus;
  reservationNumber: string | null;
  alternateReservationNumbers: string[];
  hotelId: string;
  groupId: string | null;
  channel: Channel;
  deviceId: string;
  sectionCode: string | null;
  errorCode: string | null;
  metadata: Record<string, string> | null;
  createdAt: string;
  receivedAt: string;
  displayOperationType: string;
}

// ============================================
// ERROR MONITORING TYPES
// ============================================

export interface ErrorEvent {
  id: string;
  eventCode: string;
  deviceId: string;
  hotelId: string;
  sectionCode?: string;
  reservationNumber?: string;
  errorCode?: string;
  createdAt: string;
  receivedAt: string;
  metadata?: Record<string, string>;
  errorType: 'Generic' | 'KeyDispenser' | 'Payment';
  errorDescription?: string;
}

export interface ErrorStats {
  totalErrors: number;
  genericErrors: number;
  keyDispenserErrors: number;
  paymentErrors: number;
  errorsByDevice: ErrorByDevice[];
  errorsByHotel: ErrorByHotel[];
  errorTrend: ErrorTrend[];
}

export interface ErrorByDevice {
  deviceId: string;
  /** The device's registered name, when known. */
  deviceName?: string | null;
  deviceType: string;
  hotelId: string;
  errorCount: number;
  lastErrorAt: string;
}

export interface ErrorByHotel {
  hotelId: string;
  errorCount: number;
  affectedDevices: number;
  lastErrorAt: string;
}

export interface ErrorTrend {
  date: string;
  errorCount: number;
  errorType?: string;
}

export interface ErrorFilters {
  hotelId?: string;
  hotelIds?: string; // comma-separated, from the shared multi-hotel scope selector
  groupId?: string;
  deviceId?: string;
  sectionCode?: string;
  dateFrom?: string;
  dateTo?: string;
  severity?: string;    // Filter by severity: Critical, High, Medium, Low, Info
  errorCode?: string;   // Filter by specific error code
  category?: string;    // Filter by error category: Mechanical, PMS, QR, Encoder, etc.
  nature?: string;      // Filter by nature: Error, Operational, Informational
}

// ============================================
// ABANDONMENT ANALYSIS TYPES
// ============================================

export interface AbandonmentEvent {
  id: string;
  deviceId: string;
  hotelId: string;
  sectionCode: string;
  reservationNumber?: string;
  createdAt: string;
  receivedAt: string;
  metadata?: Record<string, string>;
  sectionName: string;
  durationInSection?: number;
}

export interface AbandonmentAnalysis {
  totalAbandonments: number;
  totalCheckIns: number;
  abandonmentRate: number;
  bySection: AbandonmentBySection[];
  byDevice: AbandonmentByDevice[];
  byHour: AbandonmentByHour[];
}

export interface AbandonmentBySection {
  sectionCode: string;
  sectionName: string;
  abandonmentCount: number;
  percentage: number;
  averageDurationInSection?: number;
}

export interface AbandonmentByDevice {
  deviceId: string;
  hotelId: string;
  abandonmentCount: number;
  totalCheckIns: number;
  abandonmentRate: number;
}

export interface AbandonmentByHour {
  hour: number;
  abandonmentCount: number;
  totalCheckIns: number;
  abandonmentRate: number;
}

export interface AbandonmentFilters {
  hotelId?: string;
  groupId?: string;
  deviceId?: string;
  sectionCode?: string;
  dateFrom?: string;
  dateTo?: string;
}

// ============================================
// PAGINATION TYPE
// ============================================

export interface PaginatedResult<T> {
  items: T[];
  totalItems: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ============================================
// HELPER FUNCTIONS FOR ERROR TYPES
// ============================================

/**
 * Error type information with translation keys
 * Use with translate function: translate('resources.errorMonitor.errorTypes.generic.name')
 */
export const ERROR_TYPE_INFO = {
  Generic: {
    nameKey: 'resources.errorMonitor.errorTypes.generic.name',
    descriptionKey: 'resources.errorMonitor.errorTypes.generic.description',
    icon: '⚠️',
    color: '#ff9800',
  },
  KeyDispenser: {
    nameKey: 'resources.errorMonitor.errorTypes.keyDispenser.name',
    descriptionKey: 'resources.errorMonitor.errorTypes.keyDispenser.description',
    icon: '🔑',
    color: '#f44336',
  },
  Payment: {
    nameKey: 'resources.errorMonitor.errorTypes.payment.name',
    descriptionKey: 'resources.errorMonitor.errorTypes.payment.description',
    icon: '💳',
    color: '#e91e63',
  },
} as const;

export type ErrorType = keyof typeof ERROR_TYPE_INFO;

/**
 * Get error type information with translated labels
 * @param type - Error type key
 * @param translate - Optional translate function from react-admin
 */
export const getErrorTypeInfo = (type: string, translate?: (key: string) => string) => {
  const info = ERROR_TYPE_INFO[type as ErrorType];
  
  if (!info) {
    return {
      name: translate 
        ? translate('resources.errorMonitor.errorTypes.unknown.name')
        : 'Unknown Error',
      description: translate
        ? translate('resources.errorMonitor.errorTypes.unknown.description')
        : 'Unknown error type',
      icon: '❓',
      color: '#9e9e9e',
    };
  }
  
  return {
    name: translate ? translate(info.nameKey) : info.nameKey,
    description: translate ? translate(info.descriptionKey) : info.descriptionKey,
    icon: info.icon,
    color: info.color,
  };
};

// ============================================
// HELPER FUNCTIONS FOR SECTIONS
// ============================================

/**
 * Section information with translation keys
 * Use with translate function: translate('resources.errorMonitor.sections.s.name')
 */
export const SECTION_INFO = {
  S: {
    nameKey: 'resources.errorMonitor.sections.s.name',
    descriptionKey: 'resources.errorMonitor.sections.s.description',
    icon: '🔍',
    color: '#2196f3',
  },
  R: {
    nameKey: 'resources.errorMonitor.sections.r.name',
    descriptionKey: 'resources.errorMonitor.sections.r.description',
    icon: '🏠',
    color: '#9c27b0',
  },
  I: {
    nameKey: 'resources.errorMonitor.sections.i.name',
    descriptionKey: 'resources.errorMonitor.sections.i.description',
    icon: '👤',
    color: '#ff9800',
  },
  F: {
    nameKey: 'resources.errorMonitor.sections.f.name',
    descriptionKey: 'resources.errorMonitor.sections.f.description',
    icon: '✍️',
    color: '#795548',
  },
  U: {
    nameKey: 'resources.errorMonitor.sections.u.name',
    descriptionKey: 'resources.errorMonitor.sections.u.description',
    icon: '🎁',
    color: '#00bcd4',
  },
  P: {
    nameKey: 'resources.errorMonitor.sections.p.name',
    descriptionKey: 'resources.errorMonitor.sections.p.description',
    icon: '💳',
    color: '#4caf50',
  },
  K: {
    nameKey: 'resources.errorMonitor.sections.k.name',
    descriptionKey: 'resources.errorMonitor.sections.k.description',
    icon: '🔑',
    color: '#f44336',
  },
} as const;

export type SectionInfoCode = keyof typeof SECTION_INFO;

/**
 * Get section information with translated labels
 * @param code - Section code (S, R, I, F, U, P, K)
 * @param translate - Optional translate function from react-admin
 */
export const getSectionInfo = (code: string, translate?: (key: string) => string) => {
  const info = SECTION_INFO[code as SectionInfoCode];
  
  if (!info) {
    return {
      name: translate
        ? translate('resources.errorMonitor.sections.unknown.name')
        : 'Unknown',
      description: translate
        ? translate('resources.errorMonitor.sections.unknown.description')
        : 'Unknown section',
      icon: '❓',
      color: '#9e9e9e',
    };
  }
  
  return {
    name: translate ? translate(info.nameKey) : info.nameKey,
    description: translate ? translate(info.descriptionKey) : info.descriptionKey,
    icon: info.icon,
    color: info.color,
  };
};

// ============================================
// HELPER FUNCTIONS FOR LOCATORS
// ============================================

/**
 * Format all reservation locators for display
 * Example: "BKNG123456 / RES-2024-001 / ABC123"
 */
export const formatReservationLocators = (
  primaryLocator: string, 
  alternateLocators?: string[]
): string => {
  if (!alternateLocators || alternateLocators.length === 0) {
    return primaryLocator;
  }
  
  const allLocators = [primaryLocator, ...alternateLocators];
  return allLocators.join(' / ');
};

/**
 * Get all reservation numbers (primary + alternates)
 * Used for searching and matching
 */
export const getAllReservationNumbers = (
  primaryLocator: string,
  alternateLocators?: string[]
): string[] => {
  const allNumbers = [primaryLocator];
  if (alternateLocators && alternateLocators.length > 0) {
    allNumbers.push(...alternateLocators);
  }
  return allNumbers;
};

// ============================================
// FORM DATA TYPES
// ============================================

export interface UserFormData {
  name: string;
  email: string;
  role: UserRole;
  password?: string;
  hotelId?: string;
  groupId?: string;
}

export interface GroupFormData {
  name: string;
  description?: string;
}

export interface HotelFormData {
  name: string;
  groupId: string;
  address?: string;
  city?: string;
  country?: string;
  phone?: string;
  email?: string;
  defaultLanguage: string;
  installationCode?: string;
}

export interface DeviceFormData {
  installationCode: string;
  hotelId: string;
  groupId?: string;
  deviceType: DeviceType;
  name?: string;
}

// ============================================
// FILTER AND QUERY TYPES
// ============================================

export interface DateRangeFilter {
  dateFrom?: string;
  dateTo?: string;
}

export interface CheckInFilter extends DateRangeFilter {
  hotelId?: string;
  groupId?: string;
  channel?: Channel;
  type?: CheckInType;
}

export interface JourneyFilter extends DateRangeFilter {
  hotelId?: string;
  groupId?: string;
  isComplete?: boolean;
}

export interface DeviceFilter {
  hotelId?: string;
  groupId?: string;
  type?: DeviceType;
  status?: DeviceStatus;
}

export interface AlertFilter {
  hotelId?: string;
  groupId?: string;
  type?: AlertType;
  status?: AlertStatus;
  severity?: Alert['severity'];
}

// ============================================
// DASHBOARD AND STATISTICS TYPES
// ============================================

/**
 * Dashboard KPIs - Main metrics for the dashboard
 * 
 * Note: channelDistribution uses Record<Channel, number> to ensure
 * type safety with PascalCase channel names (Online, Kiosk, etc.)
 */
export interface DashboardKPIs {
  totalCheckIns: number;
  preCheckInCompletionRate: number;
  avgKioskCheckInTime: number | null;
  keysDispensedAutomatically: number;
  channelDistribution: ChannelDistribution;
  // Period aggregates stored in DailyAnalytics, surfaced alongside the journey
  // analytics endpoint (see reports/wave3-ui.md).
  errorCount?: number;
  abandonmentCount?: number;
  walkingCheckIns?: number;
  totalAmountCharged?: number;
}

export interface TrendsData {
  checkInsByHour: HourlyTrend[];
  // Arrival-hour distribution scoped to kiosk check-ins (added in API 1.13.38).
  kioskCheckInsByHour?: HourlyTrend[];
  last7Days: DailyTrend[];
}

export interface HourlyTrend {
  hour: number;
  count: number;
}

export interface DailyTrend {
  date: string;
  count: number;
}

export interface JourneyInsights {
  completeDigitalJourneys: number;
  timeSavedInReception: string;
  avgJourneyDuration?: number;
  mostUsedChannel?: Channel;
  // Period totals (added in API 1.13.38).
  totalJourneys?: number;
  abandonedJourneys?: number;
  abandonmentRate?: number;
}

export interface DashboardData {
  period: PeriodInfo;
  kpIs: DashboardKPIs;
  trends: TrendsData;
  journeyInsights: JourneyInsights;
  // Per-installation check-in totals, most active first (added in API 1.18.0).
  checkInsByHotel?: CheckInsByHotel[];
}

export interface CheckInsByHotel {
  hotelId: string;
  checkIns: number;
}

export interface PeriodInfo {
  from: string;
  to: string;
}


export interface CheckInStats {
  total: number;
  byChannel: Record<Channel, number>;
  byType: Record<CheckInType, number>;
  avgDuration: number;
  totalRevenue: number;
}

export interface DeviceStats {
  total: number;
  online: number;
  offline: number;
  error: number;
  byType: Record<DeviceType, number>;
}

// ============================================
// VALIDATION ERROR TYPES
// ============================================

export interface UserValidationErrors {
  name?: string;
  email?: string;
  password?: string;
  role?: string;
  hotelId?: string;
  groupId?: string;
}

export interface GroupValidationErrors {
  name?: string;
  description?: string;
}

export interface HotelValidationErrors {
  name?: string;
  groupId?: string;
  address?: string;
  email?: string;
  phone?: string;
  defaultLanguage?: string;
  installationCode?: string;
}

export interface DeviceValidationErrors {
  installationCode?: string;
  hotelId?: string;
  groupId?: string;
  deviceType?: string;
  status?: string;
}

/**
 * Get device display name: "installationCode - name" when name exists, otherwise just "installationCode"
 */
export const getDeviceDisplayName = (device: { installationCode: string; name?: string | null }): string => {
  return device.name ? `${device.installationCode} - ${device.name}` : device.installationCode;
};

/**
 * Get device type color for badges
 */
export const getDeviceTypeColor = (deviceType: DeviceType): 'primary' | 'secondary' | 'success' | 'info' => {
  switch (deviceType) {
    case 'Kiosk':
      return 'primary';
    case 'Dispenser':
      return 'secondary';
    case 'Mobile':
      return 'success';
    case 'Virtual':
      return 'info';
    default:
      return 'primary';
  }
};

/**
 * Get device status color for badges
 */
export const getDeviceStatusColor = (status: DeviceStatus): 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'PendingActivation':
      return 'warning';
    case 'Inactive':
      return 'error';
    default:
      return 'warning';
  }
};

/**
 * Get device source color for badges
 */
export const getDeviceSourceColor = (source: DeviceSource): 'info' | 'default' => {
  switch (source) {
    case 'Updater':
      return 'info';
    case 'Manual':
      return 'default';
    default:
      return 'default';
  }
};

/**
 * Get color based on minutes since last event (for DeviceStatus monitoring)
 */
export const getDeviceActivityColor = (minutes: number): 'success' | 'warning' | 'error' => {
  if (minutes < 5) return 'success';
  if (minutes < 15) return 'warning';
  return 'error';
};

/**
 * Get activity status translation key
 * Returns translation key for current activity status
 */
export const getDeviceActivityStatusKey = (minutes: number): string => {
  if (minutes < 5) return 'devices.activity.online';
  if (minutes < 15) return 'devices.activity.idle';
  return 'devices.activity.offline';
};

/**
 * Mask API key for display (show first 4 and last 4 chars)
 */
export const maskApiKey = (apiKey: string): string => {
  if (!apiKey || apiKey.length < 8) return '••••••••';
  return `${apiKey.substring(0, 4)}••••${apiKey.substring(apiKey.length - 4)}`;
};

/**
 * Normalize device data from API response
 */
export const normalizeDevice = (apiDevice: any): Device => ({
  id: apiDevice.id,
  installationCode: apiDevice.installationCode,
  hotelId: apiDevice.hotelId,
  groupId: apiDevice.groupId ?? null,
  deviceType: apiDevice.deviceType as DeviceType,
  status: apiDevice.status as DeviceStatus,
  source: apiDevice.source as DeviceSource,
  channel: apiDevice.channel as Channel,
  registeredAt: apiDevice.registeredAt,
  lastSeenAt: apiDevice.lastSeenAt ?? null,
  name: apiDevice.name ?? undefined,
});

/**
 * Prepare device data for API create request
 */
export const prepareDeviceCreateForAPI = (data: DeviceCreateFormData): any => {
  return {
    installationCode: data.installationCode,
    hotelId: data.hotelId,
    deviceType: data.deviceType,
    ...(data.groupId && { groupId: data.groupId }),
    ...(data.name && { name: data.name }),
  };
};

/**
 * Prepare device data for API update request
 */
export const prepareDeviceUpdateForAPI = (data: DeviceUpdateFormData): any => {
  const payload: any = {};
  
  if (data.hotelId !== undefined) {
    payload.hotelId = data.hotelId;
  }
  
  if (data.groupId !== undefined) {
    payload.groupId = data.groupId;
  }
  
  if (data.status !== undefined) {
    payload.status = data.status;
  }

  if (data.name !== undefined) {
    payload.name = data.name;
  }

  return payload;
};

// ============================================
// SYSTEM ADMIN PANEL TYPES
// ============================================

export interface SystemInfo {
  application: {
    version: string;
    environment: string;
    uptime: string;
    serverTime: string;
    machineName: string;
    processorCount: number;
    osDescription: string;
    dotnetVersion: string;
    hangfireServerName: string;
  };
  database: {
    isConnected: boolean;
    provider: string;
    pendingMigrations: number;
    appliedMigrations: number;
    databaseSize: string;
  };
  statistics: {
    totalDevices: number;
    onlineDevices: number;
    totalHotels: number;
    totalGroups: number;
    totalEventsToday: number;
    totalCheckInsToday: number;
    activeAlerts: number;
  };
  secrets: Array<{
    name: string;
    key: string;
    isConfigured: boolean;
    isRequired: boolean;
  }>;
  logging: {
    seq: {
      enabled: boolean;
      serverUrl: string;
      hasApiKey: boolean;
    };
    s3: {
      enabled: boolean;
      serviceUrl: string;
      bucket: string;
      hasCredentials: boolean;
    };
    installationId: string;
    appName: string;
    seqFilter: string;
  };
  externalServices: Array<{
    name: string;
    url: string;
    isReachable: boolean;
    latencyMs: number | null;
    error?: string;
  }>;
  healthChecks: Array<{
    name: string;
    status: string;
    description: string;
    durationMs: number;
  }>;
}

export interface SystemJob {
  id: string;
  displayName: string;
  description: string;
  queue: string;
  cronExpression: string;
  cronDescription: string;
  lastExecution: string | null;
  lastDuration: string | null;
  lastState: string | null;
  nextExecution: string | null;
  error?: string;
}